import {
  Button,
  Empty,
  Image,
  Input,
  message,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import React, { type FC, useEffect, useRef, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';

import useMessages from '@/lib/hooks/useMessages';
import useUser from '@/lib/hooks/useUser';
import type { Feedback } from '@/types/database/feedback';

import styles from './index.module.scss';
import Message from './Message';

const { Title, Text } = Typography;

const ModalBody: FC<{
  feedback: Feedback;
}> = ({ feedback }) => {
  const [uploadList, setUploadList] = useState<UploadFile[]>([]);
  const [newMessage, setNewMessage] = useState('');

  const { messages, sendMessage, fetchMore } = useMessages(
    'feedback',
    feedback._id
  );

  const { data: user } = useUser();

  const currentImage = uploadList?.[0]?.originFileObj;

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = async () => {
    try {
      if (currentImage) {
        await sendMessage(currentImage);
        setUploadList([]);
      }
      if (newMessage) {
        await sendMessage(newMessage);
        setNewMessage('');
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
      if (messagesRef.current) {
        messagesRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error(error);
      message.error('發送失敗');
    }
  };

  return (
    <div className={styles.modalBody}>
      <div className={styles.details}>
        <Title level={5} type="secondary">
          戶號
        </Title>
        <Text>{feedback.householdNumber}</Text>
        <Title level={5} type="secondary">
          類別
        </Title>
        <Text>{feedback.type}</Text>
        <Title level={5} type="secondary">
          內容
        </Title>
        <Text>{feedback.content}</Text>
        <Title level={5} type="secondary">
          附加照片
        </Title>
        {feedback.image?.path ? (
          <Image src={feedback.image?.path} width={100} height={100} />
        ) : (
          <Text>無</Text>
        )}
      </div>
      <div className={styles.reply}>
        <Title level={5} type="secondary">
          訊息回覆
        </Title>
        <div className={styles.messagesBlock}>
          {!messages.length && (
            <div className={styles.empty}>
              <Empty description="尚無訊息" />
            </div>
          )}
          {!!messages.length && (
            <div
              className={styles.messages}
              ref={messagesRef}
              onScroll={(e) => {
                const nativeTarget = e.nativeEvent.target;
                if (nativeTarget instanceof HTMLDivElement) {
                  const { scrollTop, scrollHeight, offsetHeight } =
                    nativeTarget;
                  const scrollDistance = scrollTop > 0 ? scrollTop : -scrollTop;
                  if (scrollHeight - offsetHeight - scrollDistance < 100) {
                    fetchMore();
                  }
                }
              }}
            >
              {messages.map((message) => (
                <Message
                  key={message._id}
                  message={message}
                  type={message.sender.id === user?._id ? 'sent' : 'received'}
                />
              ))}
            </div>
          )}
        </div>
        <div className={styles.inputs}>
          <Upload
            beforeUpload={() => false}
            showUploadList={false}
            onChange={({ fileList }) => setUploadList(fileList)}
            fileList={uploadList}
            maxCount={1}
          >
            <Button>上傳圖片</Button>
          </Upload>
          <div className={styles.inputArea}>
            {currentImage && (
              <div className={styles.imageBox}>
                <Image
                  src={URL.createObjectURL(currentImage)}
                  width={100}
                  height={100}
                />
                <button onClick={() => setUploadList([])}>
                  <AiOutlineClose />
                </button>
              </div>
            )}
            <Input.TextArea
              placeholder="請輸入訊息"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              bordered={false}
              autoSize={{ minRows: 1, maxRows: 4 }}
              ref={inputRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          <Button type="primary" onClick={handleSubmit}>
            送出
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalBody;
